import { Component, type ErrorInfo, type ReactNode } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './state/auth';
import { ToastProvider } from './components/toast';
import { PageLoading } from './components/ui';
import { reportError } from './lib/errorReporter';
import { InvitePage, LegacyLinkPage, LoginPage, ProjectEntryPage } from './pages/Login';
import { DashboardPage } from './pages/Dashboard';
import { ProjectPage } from './pages/Project';
import { ReviewPage } from './pages/Review';
import { AccountPage, ApiKeysPage, DebugPage, UsersPage } from './pages/Settings';

/** Last line of defence: a render crash shows a recovery screen and is logged. */
class ErrorBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    reportError('react.render', error.message, error.stack, { componentStack: info.componentStack?.slice(0, 1500) });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="center-fill" style={{ minHeight: '100%' }} role="alert">
        <h2>Something broke on this screen</h2>
        <p className="muted">The problem has been reported. Reloading usually fixes it.</p>
        <button className="btn btn-primary" onClick={() => location.reload()}>
          Reload
        </button>
      </div>
    );
  }
}

/** Home, unless the URL is an old `/?token=…` share link. */
function Home() {
  const [params] = useSearchParams();
  const token = params.get('token');
  if (token) return <LegacyLinkPage token={token} />;
  return (
    <RequireAuth>
      <DashboardPage />
    </RequireAuth>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <PageLoading />;
  if (!user) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <>{children}</>;
}

export default function App() {
  return (
    <ErrorBoundary>
      <ToastProvider>
        <AuthProvider>
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/p/:slug" element={<ProjectEntryPage />} />
              <Route path="/invite/:token" element={<InvitePage />} />
              <Route path="/project/:token" element={<LegacyLinkPage />} />
              <Route path="/" element={<Home />} />
              <Route path="/projects/:projectId" element={<RequireAuth><ProjectPage /></RequireAuth>} />
              <Route path="/projects/:projectId/items/:itemId" element={<RequireAuth><ReviewPage /></RequireAuth>} />
              <Route path="/settings/users" element={<RequireAuth><UsersPage /></RequireAuth>} />
              <Route path="/settings/api-keys" element={<RequireAuth><ApiKeysPage /></RequireAuth>} />
              <Route path="/settings/debug" element={<RequireAuth><DebugPage /></RequireAuth>} />
              <Route path="/account" element={<RequireAuth><AccountPage /></RequireAuth>} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ToastProvider>
    </ErrorBoundary>
  );
}
